package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.logic.NavigatorCompanyResponsiblePersonEdit
import com.example.mystybag.appCore.model.pages.CompanyResponsiblePersonEditUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantMenuDataRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateCompanyResponsiblePersonRequestModel
import kotlinx.coroutines.launch

class CompanyResponsiblePersonEditViewModel(
    context: AppContext,
    config: NavigatorCompanyResponsiblePersonEdit
) : ChildViewModel<CompanyResponsiblePersonEditUIState>(context, CompanyResponsiblePersonEditUIState(personId = config.personId)) {

    override fun onCreate() {
        navControls.isVisible = true
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.edit_short)
        loadCountries()
        loadPersonAndPreFill()
    }

    private fun loadPersonAndPreFill() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        val personId = uiState.value.personId
        scope.launch {
            updateState { it.copy(isInitialLoad = true) }
            commandBlock(showProgress = false) {
                CommonCommands.getMerchantMenuData(
                    GetMerchantMenuDataRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantMenuDataResponseModel
                                val person = responseModel?.companyResponsiblePersons?.find { (it.id ?: 0L) == personId }
                                if (person != null) {
                                    var phoneNumber = person.phoneNumber?.filter { it.isDigit() } ?: ""
                                    if (phoneNumber.startsWith("0")) phoneNumber = phoneNumber.drop(1)
                                    if (phoneNumber.startsWith("359")) phoneNumber = phoneNumber.drop(3)
                                    updateState {
                                        it.copy(
                                            firstName = person.firstName ?: "",
                                            lastName = person.lastName ?: "",
                                            phoneNumber = phoneNumber,
                                            dialCode = person.dialCode ?: "",
                                            isInitialLoad = false
                                        )
                                    }
                                } else {
                                    updateState { it.copy(isInitialLoad = false) }
                                }
                            }
                        },
                        baseError = {
                            launchOnMain {
                                updateState { it.copy(isInitialLoad = false) }
                            }
                        }
                    )
            }
        }
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val countries = response.countriesList?.map { country ->
                                    CountryItem(
                                        id = country.id,
                                        name = country.name,
                                        dialCode = country.dialCode,
                                        isoAlpha2 = country.isoAlpha2
                                    )
                                } ?: emptyList()
                                updateState { it.copy(countries = countries) }
                            }
                        }
                    )
            }
        }
    }

    fun showDialCodePicker() {
        updateState { it.copy(showDialCodePicker = true) }
    }

    fun hideDialCodePicker() {
        updateState { it.copy(showDialCodePicker = false) }
    }

    fun selectDialCodeCountry(dialCode: String) {
        updateState {
            it.copy(
                dialCode = dialCode,
                showDialCodePicker = false,
                dialCodeIsError = false,
                dialCodeErrorMsg = ""
            )
        }
    }

    fun onFirstNameChange(value: String) {
        updateState { it.copy(firstName = value, firstNameIsError = false, firstNameErrorMsg = "") }
    }

    fun onLastNameChange(value: String) {
        updateState { it.copy(lastName = value, lastNameIsError = false, lastNameErrorMsg = "") }
    }

    fun onPhoneNumberChange(value: String) {
        var cleaned = value.filter { it.isDigit() }
        if (cleaned.startsWith("0")) cleaned = cleaned.drop(1)
        if (cleaned.startsWith("359")) cleaned = cleaned.drop(3)
        updateState { it.copy(phoneNumber = cleaned, phoneNumberIsError = false, phoneNumberErrorMsg = "") }
    }

    fun onSubmit() {
        if (!validate()) return
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.updateCompanyResponsiblePerson(
                    UpdateCompanyResponsiblePersonRequestModel(
                        id = uiState.value.personId,
                        firstName = uiState.value.firstName.trim(),
                        lastName = uiState.value.lastName.trim(),
                        phoneNumber = uiState.value.phoneNumber,
                        dialCode = uiState.value.dialCode
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(sharedString(SR.strings.responsible_person_updated_successfully))
                                goBack()
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }

    private fun validate(): Boolean {
        var valid = true
        val s = uiState.value
        if (s.firstName.isBlank()) {
            updateState {
                it.copy(
                    firstNameIsError = true,
                    firstNameErrorMsg = sharedString(SR.strings.name_required)
                )
            }
            valid = false
        }
        if (s.lastName.isBlank()) {
            updateState {
                it.copy(
                    lastNameIsError = true,
                    lastNameErrorMsg = sharedString(SR.strings.name_required)
                )
            }
            valid = false
        }
        if (s.dialCode.isBlank()) {
            updateState {
                it.copy(
                    dialCodeIsError = true,
                    dialCodeErrorMsg = sharedString(SR.strings.invalid_phone_number)
                )
            }
            valid = false
        }
        if (s.phoneNumber.isBlank()) {
            updateState {
                it.copy(
                    phoneNumberIsError = true,
                    phoneNumberErrorMsg = sharedString(SR.strings.invalid_phone_number)
                )
            }
            valid = false
        }
        return valid
    }
}
